import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import uniqBy from "lodash/uniqBy";

import { useLazyQuery } from "@stadium/services/apollo-client";
import { useInfiniteScrollIntersectionObserverBased } from "@stadium/hooks/lib/use-infinite-scroll-intersectionObserver-based";

import { fetchBaseProductsList } from "@graphql/queries/stores/products/fetch-base-products-list";
import { searchProductsSerializer } from "@serializers/search-products";

import { useSendItemsContext } from "@contexts/send-items-context";

import {
  getCommonElasticSearchStoreVariables,
  getStoreParentId,
  getStoreID,
  getIsLegacyLockerShop,
} from "@store/selectors/store";
import { getCountry } from "@selectors/country";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";
import { isGlobalCountry } from "@stadium/helpers/country/utils";

import { shopOurCatalog } from "@constants/widgets";

const emptyArr = [];
const INITIAL_PAGE = 1;

const useFetchMenuIdBasedProductList = ({
  variables,
  maxProductsToFetch = 0,
  numberOfVisibleItems = 0,
  intent = "",
  gqlQuery = fetchBaseProductsList,
}) => {
  const country = useSelector(getCountry);
  const countryCode = country?.iso;
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const [fetchedProducts, setFetchedProducts] = useState(emptyArr);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const { volumeDiscountProductsVariables } = useSendItemsContext();

  const isShopOurCatalogListing = intent === shopOurCatalog;

  const [loadMoreRef, page] = useInfiniteScrollIntersectionObserverBased(hasMore, INITIAL_PAGE);

  const { perPage } = variables;

  const productLimit = useMemo(() => {
    if (numberOfVisibleItems && isShopOurCatalogListing) {
      const fetchedProductsCount = fetchedProducts.length;
      const remainingProductsToBeFetched = numberOfVisibleItems - fetchedProductsCount;
      return fetchedProductsCount === 0 || remainingProductsToBeFetched > perPage
        ? null
        : remainingProductsToBeFetched;
    }
  }, [page]);

  const [getProductListing, { data, loading }] = useLazyQuery(gqlQuery, {
    variables: {
      ...variables,
      storeId: storeParentId,
      designedStoreIds: [storeId],
      ...getCommonGlobalCountryVariables({
        country: { iso: countryCode },
        isGlobalCountry: isGlobalCountry({ iso: countryCode }),
        countryKeyAttr: "countryCode",
      }),
      page,
      skipAggregations: true,
      limit: productLimit,
      showVariantsAsProducts: true,
      ...commonESVariables,
      includeProductVariants: false,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
      ...volumeDiscountProductsVariables,
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data) {
      const products = searchProductsSerializer(data?.baseSearch?.products || emptyArr);
      const total = data?.baseSearch?.total || 0;
      const updatedProducts = uniqBy([...fetchedProducts, ...products], (obj) => obj.id);
      setFetchedProducts(updatedProducts);
      setHasMore(Math.min(maxProductsToFetch, total) > updatedProducts.length);
      setTotalProducts(total);
    }
  }, [data]);

  return {
    loading,
    fetchedProducts,
    totalProducts,
    loadMoreRef,
    getProductListing,
  };
};

export default useFetchMenuIdBasedProductList;
